// @ts-strict-ignore
import React, {useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import {useVirtualizer} from '@tanstack/react-virtual';

import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {useTranslation} from 'common/i18n/hooks';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {usePrevious} from 'common/hooks/usePrevious';
import {isServerSideStitchingEnabled} from 'common/report/util/feature-sampling';
import {useCurrentProject} from 'common/contexts/react/current-project-context';

import {RecordingEventMetadata, RecordingMetadata} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {REPLAY_EVENT} from './query-utils';
import {formatRecordingEventTime, getSearchHighlightPieces} from './util';
import {EventIndicator} from './event-indicator';

type ReplayPlaylistRowEventMetadataProps = {
  loadingEventMetadata: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  jumpToEvent: (time: number, replayId: string) => void;
  currentRecordingProgressTime: number;
  searchFilter: string;
  isStitchingEnabled: boolean;
};

const MAX_NUM_EVENT_ANIMATIONS = 7;
const STAGGERED_ANIMATION_DELAY_MS = 350;
const STAGGERED_ANIMATION_DURATION_MS = 100;
const REPLAY_EVENT_HEIGHT = 54;

function replayPlaylistRowEventMetadata(props: ReplayPlaylistRowEventMetadataProps) {
  const [userInteracting, setUserInteracting] = useState(false);

  const eventMetadataContainerRef = useRef<HTMLDivElement>(null);
  const scrollToEventTimeoutRef = useRef<number | null>(null);
  const animatedStitchedEventCountRef = useRef(0);
  const currentProject = useCurrentProject();
  const isSSSEnabled = isServerSideStitchingEnabled(currentProject.get().value);
  const previousIsStitchingEnabled = usePrevious(props.isStitchingEnabled);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const forceUpdateScrollRef = useRef(false);

  const {t} = useTranslation();

  const existingEventMetadata = props.replayEvents || [];

  let sortedEventMetadata = existingEventMetadata
    .sort((a, b) => a.eventTime - b.eventTime)
    .filter((event) => event.eventName !== REPLAY_EVENT);

  if (props.searchFilter) {
    sortedEventMetadata = sortedEventMetadata.filter((event) => {
      return getSearchHighlightPieces(event.eventName, props.searchFilter).some((piece) => piece.highlight);
    });
  }

  const virtualizer = useVirtualizer({
    count: sortedEventMetadata.length,
    estimateSize: (index) => {
      if (!props.isStitchingEnabled && !sortedEventMetadata[index].hasMatchingReplayIdProperty) {
        return 0;
      }
      return REPLAY_EVENT_HEIGHT;
    },
    getScrollElement: () => eventMetadataContainerRef.current,
    getItemKey: (index) => sortedEventMetadata[index].key,
    overscan: 10,
  });

  const virtualItems = virtualizer.getVirtualItems();
  // Instead of using absolute positioning (the default strategy for virtualizer), using a padding-based scroll manipulation
  // Absolute positioning breaks our z-index overlays like tooltips and context menus.
  const [paddingTop, paddingBottom] =
    virtualItems.length > 0
      ? [
          Math.max(0, virtualItems[0].start - virtualizer.options.scrollMargin),
          Math.max(0, virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end),
        ]
      : [0, 0];

  useEffect(() => {
    if (
      forceUpdateScrollRef.current ||
      (!scrollToEventTimeoutRef.current &&
        props.currentRecordingProgressTime &&
        props.currentRecordingProgressTime < props.replay.time + props.replay.duration / 1000 &&
        props.currentRecordingProgressTime > props.replay.time)
    ) {
      scrollContainerToSelectedEvent(!forceUpdateScrollRef.current);
    }
    forceUpdateScrollRef.current = false;
  }, [props.currentRecordingProgressTime]);

  useEffect(() => {
    // don't animate on the first render, only when stitching changes
    if (previousIsStitchingEnabled === undefined) {
      setShouldAnimate(true);
      return () => {};
    }

    virtualizer.measure();

    // make sure scroll is immediately updated to the selected event in case the ref changes
    forceUpdateScrollRef.current = true;
    scrollContainerToSelectedEvent(false);

    // calculate how long the animation will take and stop auto scrolling until it's done
    // scroll to to the selected event once it's done in case we're actively autoscrolling
    const scrollDelay = (animatedStitchedEventCountRef.current - 1) * STAGGERED_ANIMATION_DELAY_MS;
    scrollToEventTimeoutRef.current = window.setTimeout(() => {
      window.clearTimeout(scrollToEventTimeoutRef.current);
      scrollToEventTimeoutRef.current = null;
      forceUpdateScrollRef.current = false;
      scrollContainerToSelectedEvent();
    }, scrollDelay);

    return () => {
      window.clearTimeout(scrollToEventTimeoutRef.current);
    };
  }, [props.isStitchingEnabled]);

  const getSelectedEventIndex = (eventMetadata: RecordingEventMetadata[]) => {
    if (!props.currentRecordingProgressTime) {
      return -1;
    }

    let minDistance = Number.POSITIVE_INFINITY;
    let selectedIndex = -1;
    eventMetadata.forEach((event, i) => {
      if (!props.isStitchingEnabled && !event.hasMatchingReplayIdProperty) {
        return;
      }

      const distance = Math.abs(event.eventTime - props.currentRecordingProgressTime);
      if (distance < minDistance) {
        selectedIndex = i;
        minDistance = distance;
      }
    });

    return selectedIndex;
  };

  function scrollContainerToSelectedEvent(smooth = true) {
    if (!userInteracting && !props.searchFilter && eventMetadataContainerRef.current) {
      virtualizer.scrollToIndex(getSelectedEventIndex(sortedEventMetadata), {
        behavior: smooth ? `smooth` : `auto`,
        align: `start`,
      });
    }
  }

  function maybeShowSearchHighlight(inputText: string) {
    const pieces = getSearchHighlightPieces(inputText, props.searchFilter);

    return (
      <>
        {pieces.map((piece, i) => {
          return (
            <span className={piece.highlight ? styles.replaysTextHighlight : ``} key={i}>
              {piece.text}
            </span>
          );
        })}
      </>
    );
  }

  const selectedEventIndex = getSelectedEventIndex(sortedEventMetadata);

  return (
    <div
      className={`${styles.replaysListItemEventExpansion} ${!props.currentRecordingProgressTime && styles.replaysListItemEventExpansionBordered}`}
      ref={eventMetadataContainerRef}
      onMouseOver={() => {
        setUserInteracting(true);
      }}
      onMouseLeave={() => {
        setUserInteracting(false);
      }}
      role={`rowgroup`}
    >
      {props.loadingEventMetadata && existingEventMetadata.length === 0 ? (
        <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Small} role={`progressbar`} />
      ) : (
        <div
          className={styles.replaysListItemEventBody}
          style={{
            '--event-item-animation-delay-ms': STAGGERED_ANIMATION_DELAY_MS,
            '--event-item-animation-duration-ms': STAGGERED_ANIMATION_DURATION_MS,
            '--event-item-height': REPLAY_EVENT_HEIGHT,
            paddingTop,
            paddingBottom,
          }}
        >
          {sortedEventMetadata.length === 0 && (
            <div className={styles.replaysListItemEventHeaderEmpty}>{t(`session-replay.no-related-events`)}</div>
          )}
          {(() => {
            let visibleRowCount = 1;
            animatedStitchedEventCountRef.current = 1;
            return virtualItems.map((virtualItem) => {
              const event = sortedEventMetadata[virtualItem.index];
              // Minimize the number of animations we need to get the point across, otherwise UI will be doing a ton of layout / rendering
              // and look too busy especially when we're already autoscrolling..
              // Up to MAX_NUM_EVENT_ANIMATIONS - the most visible number of events in the view - will be animated.
              let animationOrder = 0;
              if (virtualItem.index > selectedEventIndex) {
                visibleRowCount++;

                if (visibleRowCount <= MAX_NUM_EVENT_ANIMATIONS && !event.hasMatchingReplayIdProperty) {
                  animationOrder = animatedStitchedEventCountRef.current++;
                }
              }

              return (
                <div
                  key={event.key}
                  className={clsx(styles.replaysListItemEvent, {
                    [styles.replaysListItemEventTimeSelected]: selectedEventIndex === virtualItem.index,
                    [styles.replaysListItemAnimated]: shouldAnimate && animationOrder > 0,
                    [styles.replaysStitchedEventHidden]: !(
                      props.isStitchingEnabled || event.hasMatchingReplayIdProperty
                    ),
                  })}
                  style={{
                    '--event-item-animation-order': animationOrder,
                  }}
                  onClick={() => {
                    props.jumpToEvent(event.eventTime, props.replay.replayId);
                  }}
                  role={`row`}
                >
                  <div className={styles.replaysListItemEventFirst}>
                    <div className={styles.replaysListItemEventTime}>
                      {formatRecordingEventTime(props.replay.time, event.eventTime)}
                    </div>
                    <div className={styles.replaysListItemEventName}>
                      <span>{maybeShowSearchHighlight(event.eventName)}</span>
                    </div>
                  </div>
                  {isSSSEnabled && (
                    <div className={styles.replaysListItemEventIndicator}>
                      <EventIndicator
                        isObserved={event.hasMatchingReplayIdProperty}
                        tooltipText={
                          event.hasMatchingReplayIdProperty
                            ? t(`session-replay.event-observed`)
                            : t(`session-replay.event-stitched`)
                        }
                      />
                    </div>
                  )}
                  <div className={styles.replaysListItemEventQuantity}>{event.quantity}</div>
                  <TooltipReact placement={Placement.RightCenter}>{event.eventName}</TooltipReact>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}

const ReplayPlaylistRowEventMetadataStyled = withStyles(replayPlaylistRowEventMetadata, css);
export const ReplayPlaylistRowEventMetadataEl = react2panel(
  `mp-replay-playlist-row-event-metadata`,
  ReplayPlaylistRowEventMetadataStyled,
);
